import { refreshEditorialImageNote } from './16-post-continuations.mjs';
import variants from '../14-public-image-variants.json' with { type: 'json' };
// Prepared detail aliases retain exact URLs when a primary is smaller than its
// source illustration. Derive this fixed public lookup once, never per feed image.
const detailAliases = new Map(
  Object.values(variants)
    .filter((entry) => entry.detail)
    .map(({ detail }) => [detail.url, detail.sources.map(({ width, url }) => ({ width, url }))]),
);
/** Use inventoried public assets or bounded canonical private-media descriptors.
 * These presentation hints never grant access; each protected URL rechecks server ACL. */
export function imageCandidates(image, supplied = []) {
  if (/^\/media\/[0-9a-f-]{36}$/.test(image ?? '') && Array.isArray(supplied)) {
    const rows = supplied.filter(
      (row) =>
        row &&
        [640, 1280].some((edge) => row.url === `${image}?size=${edge}`) &&
        Number.isInteger(row.width) &&
        row.width > 0 &&
        row.width <= 1280 &&
        Number.isInteger(row.height) &&
        row.height > 0 &&
        row.height <= 1280,
    );
    if (rows.length)
      return rows
        .sort((a, b) => a.width - b.width)
        .filter((row, index, all) => !index || row.width !== all[index - 1].width);
  }
  if (typeof image !== 'string') return null;
  if (detailAliases.has(image)) return detailAliases.get(image).map((row) => ({ ...row }));
  if (!Object.hasOwn(variants, image)) return null;
  const entry = variants[image];
  return entry.widths.map((width) => ({
    width,
    url: width === entry.width ? image : image.replace(/\.[^.]+$/, `-${width}.webp`),
  }));
}

/** Select an inventoried raster or fixed protected derivative. Unknown public URLs
 * retain their original source; old protected uploads fall back server-side without resizing. */
export function photoSource(image, size = 1280, supplied = []) {
  if (/^\/media\/[0-9a-f-]{36}$/.test(image ?? '') && !supplied.length)
    return `${image}?size=${size <= 640 ? 640 : 1280}`;
  const candidates = imageCandidates(image, supplied);
  if (!candidates) return image;
  return (candidates.find((candidate) => candidate.width >= size) ?? candidates.at(-1)).url;
}
/** Real intrinsic width descriptors, never fabricated widths or duplicate URLs. */
export function photoSet(image, supplied = []) {
  return (
    imageCandidates(image, supplied)
      ?.map((candidate) => `${candidate.url} ${candidate.width}w`)
      .join(', ') ?? ''
  );
}
/** Speculative work may fetch only an inventoried light public derivative/native
 * raster <=160px. Unknown originals (including SVG) start no speculative request. */
export function publicImageHint(image) {
  const candidates = imageCandidates(image);
  return candidates?.find((candidate) => candidate.width <= 160)?.url ?? null;
}
/** One explicit first-screen image can be eager/high. Talk attachments remain
 * collapsed, and every appended/gallery-neighbor image stays lazy/auto. */
export function postImageAttributes(image, { priority = false, talk = false, variants = [] } = {}) {
  return {
    src: photoSource(image, 640, variants),
    srcset: photoSet(image, variants),
    sizes: '(max-width:720px) 100vw, 660px',
    loading: priority && !talk ? 'eager' : 'lazy',
    fetchPriority: priority && !talk ? 'high' : 'auto',
    decoding: 'async',
  };
}

/** Only inventoried, reviewed course SVG replacements are eligible for automatic
 * recovery. These lineage hints never grant access; hydration rechecks current ACL. */
export function courseImageReplacement(post, registry = variants) {
  if (
    post?.editorial !== true ||
    post.audience !== 'public' ||
    post.kind !== 'post' ||
    post.media?.length ||
    post.sharedPost
  )
    return null;
  for (const [url, descriptor] of Object.entries(registry)) {
    const source = descriptor?.replaces;
    if (
      source?.url === post.image &&
      source.courseId === post.course?.id &&
      source.lessonId === post.course?.lessonId &&
      source.lessonRevision === post.course?.lessonRevision
    )
      return { url, descriptor };
  }
  return null;
}
const recoveryIdentity = [
  'id',
  'authorId',
  'text',
  'title',
  'audience',
  'kind',
  'course',
  'media',
  'sharedPost',
  'updatedAt',
  'imageSourceURL',
  'imageLicenseURL',
  'sourceURL',
  'sourceTitle',
  'editorialSources',
  'authorContinuations',
  'authorReadings',
];
/** Caption/source corrections and custom media are never silently paired with new
 * artwork. Engagement may change independently and is not merged by media recovery. */
export function sameImageRecoveryContent(before, current) {
  return recoveryIdentity.every(
    (key) =>
      JSON.stringify(before[key] ?? (key === 'authorReadings' ? [] : null)) ===
      JSON.stringify(current[key] ?? (key === 'authorReadings' ? [] : null)),
  );
}
/** One attempt per failed image, at most two actor-scoped reads in flight, no body
 * cache. Disposing a surface removes listeners and aborts unused reads. A result
 * may patch only the still-mounted image for the same accepted snapshot/actor. */
export function createCourseImageRecovery(
  ctx,
  {
    registry = variants,
    maxReads = 2,
    Observer = globalThis.MutationObserver,
    digest = async (value) =>
      [
        ...new Uint8Array(
          await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)),
        ),
      ]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join(''),
  } = {},
) {
  if (!Number.isInteger(maxReads) || maxReads < 1 || maxReads > 2)
    throw new RangeError('Image recovery permits one or two in-flight reads.');
  const requests = new Map(),
    bindings = new Set();
  let generation = 0,
    observer;
  function bind(image, post, { onRecovered = () => {}, onUnavailable = () => {} } = {}) {
    const replacement = courseImageReplacement(post, registry),
      snapshot = ctx.state,
      actor = snapshot?.me?.id,
      epoch = generation;
    if (!replacement || !actor || typeof ctx.api !== 'function') return () => {};
    let attempted = false,
      disposed = false,
      request;
    const binding = { dispose, image, connected: image.isConnected === true };
    bindings.add(binding);
    if (!observer && Observer && image.ownerDocument?.documentElement) {
      observer = new Observer(() => {
        for (const candidate of [...bindings]) {
          if (candidate.image.isConnected) candidate.connected = true;
          else if (candidate.connected) candidate.dispose();
        }
      });
      observer.observe(image.ownerDocument.documentElement, { childList: true, subtree: true });
    }
    function valid() {
      return (
        !disposed &&
        epoch === generation &&
        image.isConnected === true &&
        ctx.state === snapshot &&
        ctx.state?.me?.id === actor
      );
    }
    function unavailable() {
      if (!valid()) return;
      image.removeAttribute('srcset');
      image.removeAttribute('src');
      image.alt = ctx.uiText?.('Image unavailable') ?? 'Image unavailable';
      image.dataset.imageRecovery = 'unavailable';
      onUnavailable();
    }
    async function recover() {
      if (attempted || !valid()) return;
      attempted = true;
      binding.connected = true;
      const key = actor + ':' + post.id;
      request = requests.get(key);
      if (request && request.snapshot !== snapshot) {
        request.controller.abort();
        if (requests.get(key) === request) requests.delete(key);
        request = undefined;
      }
      if (!request) {
        if (requests.size >= maxReads) {
          unavailable();
          return;
        }
        const controller = new AbortController();
        request = { controller, members: new Set(), snapshot };
        requests.set(key, request);
        request.promise = Promise.resolve()
          .then(() =>
            ctx.api(
              '/api/feed/hydrate',
              {
                ids: [post.id],
                format: 'wall',
                scope: 'all',
                mode: 'latest',
                contentScope: 'learning',
              },
              { signal: controller.signal },
            ),
          )
          .finally(() => {
            if (requests.get(key) === request) requests.delete(key);
          });
      }
      const owned = request;
      owned.members.add(binding);
      try {
        const result = await owned.promise;
        if (!valid()) return;
        const current = result.posts?.find((row) => row.id === post.id);
        if (
          !current ||
          result.missingIds?.includes(post.id) ||
          current.image !== replacement.url ||
          !sameImageRecoveryContent(post, current) ||
          current.editorial !== true ||
          current.audience !== 'public' ||
          current.media?.length ||
          current.sharedPost
        ) {
          unavailable();
          return;
        }
        const proof = await digest(
          JSON.stringify([
            current.image,
            current.alt,
            current.imageCredit,
            current.imageLicense,
            current.imageChanges,
            current.imageAspectRatio,
          ]),
        );
        if (!valid()) return;
        if (proof !== replacement.descriptor.replaces.presentationSHA256) {
          unavailable();
          return;
        }
        // No state replacement, full render, media teardown or synthetic click.
        image.src = photoSource(current.image, 640);
        const srcset = photoSet(current.image);
        if (srcset) image.setAttribute('srcset', srcset);
        else image.removeAttribute('srcset');
        image.alt = current.alt ?? '';
        image.width = replacement.descriptor.width;
        image.height = replacement.descriptor.height;
        image.dataset.imageRecovery = 'recovered';
        onRecovered(current);
      } catch {
        unavailable();
      } finally {
        owned.members.delete(binding);
      }
    }
    function dispose() {
      if (disposed) return;
      disposed = true;
      image.removeEventListener('error', recover);
      bindings.delete(binding);
      if (request) {
        request.members.delete(binding);
        if (!request.members.size) request.controller.abort();
      }
    }
    image.addEventListener('error', recover);
    return dispose;
  }
  function dispose() {
    generation++;
    observer?.disconnect();
    observer = undefined;
    for (const binding of [...bindings]) binding.dispose();
    for (const request of requests.values()) request.controller.abort();
    requests.clear();
  }
  return { bind, dispose };
}

/** Attach recovery to a course card without replacing the focused open control,
 * caption, source links or draft. Later inspection uses the authorized new DTO. */
export function bindCoursePostImage(ctx, recovery, image, post, card, accept) {
  return recovery.bind(image, post, {
    onRecovered(current) {
      accept(current);
      refreshEditorialImageNote(ctx, card, current);
      if (image.parentElement?.tagName === 'BUTTON')
        image.parentElement.setAttribute('aria-label', ctx.uiText('Open photo') + ': ' + image.alt);
    },
    onUnavailable() {
      image.parentElement?.setAttribute('aria-disabled', 'true');
    },
  });
}
